/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {removeQueryString} from '@ion/core/src/utils/QueryStringUtils';
import APM from '../APM';
import {
  MonitorCategory,
  MonitorResults
} from '../types/types';

/**
 * Provides the single point of entry for sending the results of an Application
 * Performance Monitoring instance to an external service. The external service
 * from which the event is to be sent is configured via the `setClient` config
 * function.
 *
 * @param   category The `MonitorCategory` for which the measurement is scoped.
 * @param   context The category which provides additional scope for the tracked
 *          event. This value typically reflects the page url when the category
 *          is set to `MonitorCategory.Page`. Likewise, when the category is set
 *          to `MonitorCategory.Feature`, the context should be set to the name
 *          of the feature being measured. When the category argument is set to
 *          `MonitorCategory.Initialization`, the context will automatically be
 *          set to `Application`.
 * @param   payload A `MonitorResults` instance containing the values for each
 *          performance measurement associated with the monitoring occurrence.
 * @returns `true` if the monitoring event was successfully sent, otherwise,
 *          `false`.
 */
export const send = (
  category: MonitorCategory, context: string, payload: MonitorResults | Partial<MonitorResults>): boolean => {
  if (APM.isEnabled) {
    const client = APM.getBeaconClient();
    if (client) {
      const eventName = (
        category !== MonitorCategory.Initialization ? context : ''
      );
      return client.send?.(removeQueryString(eventName), payload as MonitorResults, category);
    }
  }
};
